import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { qpxToast } from '@quipex/core/utils';
import {
    injectQuippySession,
    sameQuippyScope,
    type QuippyChatEntry,
    type QuippyScope,
    type QuippyChatHandoff,
    type QuippyPendingChat,
} from '@quipex/feature/quippy-conversation';
import { chatRoute } from '../host-actions';

type QuippyChatNavigation = Parameters<QuippyChatEntry['navigate']>[0];
type QuippyChatPrompt = NonNullable<QuippyChatNavigation['prompt']>;

interface PendingChat extends QuippyPendingChat {
    readonly scope: QuippyScope;
}

@Injectable()
export class QuippyChatEntryState
    implements QuippyChatEntry, QuippyChatHandoff
{
    private readonly router = inject(Router);
    private readonly session = injectQuippySession();
    private pending: PendingChat | undefined;

    public async navigate(navigation: QuippyChatNavigation): Promise<boolean> {
        const scope = navigation.target;
        const ref = this.session.create(scope);
        const pending: PendingChat = {
            ref,
            scope,
            prompt: normalizePrompt(navigation.prompt) ?? null,
        };
        this.pending = pending;

        try {
            const commands = [...chatRoute(scope, 'new')];
            const destination = this.router.createUrlTree(commands);
            const navigated = await this.router.navigate(commands);
            const reachedDestination =
                navigated &&
                this.router.isActive(destination, {
                    paths: 'exact',
                    queryParams: 'ignored',
                    fragment: 'ignored',
                    matrixParams: 'ignored',
                });
            if (!reachedDestination) {
                this.clear(pending);
                reportNavigationFailure();
            }
            return reachedDestination;
        } catch {
            this.clear(pending);
            reportNavigationFailure();
            return false;
        }
    }

    public consume(scope: QuippyScope): QuippyPendingChat | null {
        const pending = this.pending;
        if (!pending || !sameQuippyScope(pending.scope, scope)) return null;
        this.pending = undefined;
        return { ref: pending.ref, prompt: pending.prompt };
    }

    private clear(pending: PendingChat): void {
        if (this.pending === pending) this.pending = undefined;
    }
}

export function injectQuippyChatEntry(): QuippyChatEntry {
    return inject(QuippyChatEntryState);
}

export function injectQuippyChatHandoff(): QuippyChatHandoff {
    return inject(QuippyChatEntryState);
}

function normalizePrompt(
    prompt: QuippyChatPrompt | undefined
): QuippyChatPrompt | undefined {
    if (!prompt) return undefined;
    const text = prompt.text.trim();
    return text ? { ...prompt, text } : undefined;
}

function reportNavigationFailure(): void {
    // Router failures do not pass through the HTTP auto-toast interceptor.
    // eslint-disable-next-line agentic/prefer-auto-error-toast
    qpxToast.error('Unable to open Ask Quippy. Try again.');
}
